/**
 * Primary app navigation — vertical sidebar on desktop, collapsible
 * hamburger drawer on mobile.
 *
 * Eight items:
 *   Dashboards / Reports / Trailers / Conversations   ← everyone
 *   Security & Permissions / Integrations / Settings / Audit Log   ← admin only
 *
 * Navigation model:
 *   - Top group items set `phase` directly (existing top-level phases).
 *   - Bottom admin group opens the settings modal on a specific tab
 *     in this commit. Commit 3 will convert those to full-page phases.
 *
 * Props:
 *   phase             — current phase string from App state
 *   setPhase          — phase setter
 *   authUser          — to derive isAdmin + avatar + initials
 *   onNavigate        — (phase, tab?) => void — centralized handler in App
 *                       that can also trigger side effects (openSettingsTab
 *                       on admin click, etc.)
 */

import React, { useState } from 'react';
import './LeftNav.css';

// Item structure:
//   { id, label, kind: 'phase' | 'settings', target, icon }
// target = phase string when kind='phase', tab id when kind='settings'.
const NAV_ITEMS = [
  {
    id: 'dashboards',
    label: 'Dashboards',
    kind: 'phase',
    target: 'dashboard',
    adminOnly: false,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="9" rx="1" />
        <rect x="14" y="3" width="7" height="5" rx="1" />
        <rect x="14" y="12" width="7" height="9" rx="1" />
        <rect x="3" y="16" width="7" height="5" rx="1" />
      </svg>
    ),
  },
  // Order mirrors usage frequency for a rep living here daily
  // (2026-07-26 nav audit): momentum first (Dashboards), then the
  // daily funnel surfaces (Calls → Trailers → Conversations →
  // Decision Rooms), then weekly analytics (Reports), then assets
  // (Library, QBR). Reports moved from slot #2 — it was splitting
  // the work cluster it reports on.
  {
    id: 'calls',
    label: 'Calls',
    kind: 'phase',
    target: 'calls',
    adminOnly: false,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
  },
  {
    id: 'highlight-reels',
    label: 'Trailers',
    kind: 'phase',
    target: 'library',
    adminOnly: false,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M6 4v16M18 4v16M2 8h4M2 16h4M18 8h4M18 16h4" />
      </svg>
    ),
  },
  // Conversations left the sidebar 2026-08-12 — it's an INBOX, not a
  // navigable phase, so it lives in the top ribbon next to the
  // notifications bell (with its unread badge). The phase itself and
  // its deep links are unchanged.
  {
    id: 'buyer-spaces',
    label: 'Decision Rooms',
    kind: 'phase',
    target: 'buyer-spaces',
    adminOnly: false,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    // Forms & surveys (2026-09-07) — qualification, feedback, intake
    // and win/loss forms buyers fill in a Decision Room or from a
    // direct link. Dark behind the forms flag (same requiresFlag
    // mechanism as Coaching).
    id: 'forms',
    label: 'Forms',
    kind: 'phase',
    target: 'forms',
    adminOnly: false,
    requiresFlag: 'forms',
    icon: (
      // Clipboard with a tick.
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="8" y="2" width="8" height="4" rx="1" />
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        <path d="M9 14l2 2 4-4" />
      </svg>
    ),
  },
  // Reports + QBR Briefs left the nav on 2026-08-06 (nav-merge audit):
  // both live as Dashboards tabs now — one analytics door instead of
  // three. A quarterly ritual (QBR) doesn't spend a daily slot, and
  // "Dashboards vs Reports" made users guess which door answers
  // "how is it going". Legacy deep-links to both phases still render.
  {
    // Coaching (2026-08-06) — "how is this person selling" (object =
    // the rep), vs Dashboards' "how are the deals doing" (object = the
    // deal). QBR briefs moved HERE from the Dashboards tab strip the
    // same day: a generated document broke the strip's live-analytics
    // promise, and for a small team the QBR *is* the coaching ritual.
    // Net nav 6 → 7 core items but Dashboards drops a tab. Dark behind
    // the coaching_tab flag (first flag-gated nav item — requiresFlag
    // reads authUser.company.featureFlags from /auth/me).
    id: 'coaching',
    label: 'Coaching',
    kind: 'phase',
    target: 'coaching',
    adminOnly: false,
    requiresFlag: 'coaching_tab',
    icon: (
      // Whistle: body circle + mouthpiece wedge + blow lines.
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="15" r="5" />
        <path d="M13 11.5 21 8v4l-7.5 1.5" />
        <path d="M9 15h.01" />
      </svg>
    ),
  },
  {
    // Sales Library — company-wide pool of reusable assets reps can
    // pull into any Decision Room. Distinct from "Trailers" above
    // (which is the per-rep list of trailer videos). Internal phase
    // ID is 'sales-library' to avoid colliding with the existing
    // 'library' phase the trailer list uses.
    id: 'sales-library',
    label: 'Library',
    kind: 'phase',
    target: 'sales-library',
    adminOnly: false,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    ),
  },
  // ─── admin group below ────────────────────────────────────────
  {
    id: 'security',
    label: 'Security & Permissions',
    kind: 'phase',
    target: 'security',
    adminOnly: true,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
  {
    id: 'integrations',
    label: 'Integrations',
    kind: 'phase',
    target: 'integrations',
    adminOnly: true,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="9" height="10" rx="1" />
        <rect x="13" y="7" width="9" height="10" rx="1" />
        <path d="M11 12h2" />
      </svg>
    ),
  },
  {
    id: 'settings',
    label: 'Settings',
    kind: 'phase',
    target: 'settings',
    adminOnly: true,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  },
  {
    id: 'audit-log',
    label: 'Audit Log',
    kind: 'phase',
    target: 'audit-log',
    adminOnly: true,
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="8" y1="13" x2="16" y2="13" />
        <line x1="8" y1="17" x2="16" y2="17" />
        <line x1="8" y1="9" x2="10" y2="9" />
      </svg>
    ),
  },
];

// Provider → small glyph used in the upcoming-calls widget. Inline so we
// don't have to maintain a separate icon bundle for three items.
const PROVIDER_GLYPH = {
  'google-meet': (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="6" width="14" height="12" rx="2" />
      <path d="M16 10l6-4v12l-6-4" />
    </svg>
  ),
  'microsoft-teams': (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="14" height="14" rx="2" />
      <circle cx="18" cy="11" r="3" />
    </svg>
  ),
  zoom: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="7" width="14" height="10" rx="2" />
      <path d="M22 8v8l-6-4z" />
    </svg>
  ),
};

// Per-row time (the RIGHT side of each meeting row, now that the date
// group header owns the coarse date): compact clock-only formats.
//   • Under an hour → "In 12m"
//   • Otherwise     → "3:45 PM"
function formatMeetingTime(iso) {
  if (!iso) return '';
  const when = new Date(iso);
  const now = new Date();
  const diffMs = when - now;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin >= 0 && diffMin < 60) {
    return diffMin <= 1 ? 'Now' : `In ${diffMin}m`;
  }
  return when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// Day-level header for the date separator. Returns a friendly label
// for today / tomorrow, otherwise a weekday + date like "Thu, Apr 24".
function formatMeetingDayHeader(iso) {
  if (!iso) return '';
  const when = new Date(iso);
  const now = new Date();
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(when, now)) return 'Today';
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (sameDay(when, tomorrow)) return 'Tomorrow';
  return when.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

// Day bucket key — YYYY-MM-DD in local time so sorting is stable and
// meetings land in the right group regardless of timezone weirdness.
function dayBucketKey(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function LeftNav({ phase, authUser, onNavigate, upcomingMeetings = [], upcomingMeta = null, collapsed = false, onToggleCollapse }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = !!(authUser && authUser.company && authUser.company.isAdmin);
  // UI feature flags from /auth/me (dark-launch substrate, 2026-08-06).
  // Absent map (old token, bootstrap placeholder) → flagged items hide,
  // which is the safe direction for a dark launch.
  const uiFlags = (authUser && authUser.company && authUser.company.featureFlags) || {};

  const visibleItems = NAV_ITEMS.filter(
    (it) => (!it.adminOnly || isAdmin) && (!it.requiresFlag || uiFlags[it.requiresFlag] === true)
  );

  // "Active" maps slightly fuzzily — the `library` phase is reached from
  // the Trailers item, `dashboard` from Dashboards, etc. We match
  // current phase against the item's target when kind='phase'.
  const isActive = (item) => {
    if (item.kind === 'phase') return phase === item.target;
    return false; // admin items don't set phase, they open the modal
  };

  const handleClick = (item) => {
    setMobileOpen(false);
    if (item.comingSoon) return;
    onNavigate(item);
  };

  return (
    <>
      {/* Mobile hamburger — floats top-left on small screens. Hidden
          via CSS above the breakpoint. */}
      <button
        type="button"
        className="left-nav-hamburger"
        onClick={() => setMobileOpen((v) => !v)}
        aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileOpen}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {mobileOpen ? (
            <>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </>
          ) : (
            <>
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </>
          )}
        </svg>
      </button>

      {/* Mobile backdrop — click anywhere off the drawer to dismiss. */}
      {mobileOpen && (
        <div className="left-nav-backdrop" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={`left-nav ${mobileOpen ? 'is-mobile-open' : ''} ${collapsed ? 'is-collapsed' : ''}`}
        aria-label="Primary navigation"
      >
        {/* Logo block — compact identity at the top of the sidebar. */}
        <div className="left-nav-logo">
          <div className="left-nav-logo-icon">
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" />
              <circle cx="24" cy="24" r="8" fill="currentColor" />
              <path d="M24 4 L24 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M24 36 L24 44" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M4 24 L12 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M36 24 L44 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <div className="left-nav-logo-text">
            <strong>TrailerCast&trade;</strong>
            <span>AI Sales Experience</span>
          </div>
        </div>

        <nav className="left-nav-list">
          {visibleItems.map((item, i) => {
            // Divider above the first admin-only item so the group
            // visually separates without a full header.
            const prev = visibleItems[i - 1];
            const showDivider = item.adminOnly && (!prev || !prev.adminOnly);
            return (
              <React.Fragment key={item.id}>
                {showDivider && (
                  <>
                    <div className="left-nav-divider" aria-hidden="true" />
                    {/* Group caption (2026-07-26 nav audit): 12 items
                        for admins is over the scanning ceiling — the
                        caption chunks the tail into "admin config".
                        Hidden in the collapsed rail (divider stays). */}
                    <div className="left-nav-caption" aria-hidden="true">Admin</div>
                  </>
                )}
                <button
                  type="button"
                  className={`left-nav-item ${isActive(item) ? 'is-active' : ''} ${item.comingSoon ? 'is-coming-soon' : ''}`}
                  onClick={() => handleClick(item)}
                  disabled={item.comingSoon}
                  // data-label feeds the collapsed-mode CSS tooltip. The
                  // real accessible name stays in the (visually
                  // collapsed, never removed) label span. Native title
                  // only in expanded mode — it would double up with the
                  // CSS tooltip on the rail.
                  data-label={item.label}
                  title={item.comingSoon ? 'Coming soon' : (collapsed ? undefined : item.label)}
                  aria-current={isActive(item) ? 'page' : undefined}
                >
                  <span className="left-nav-item-icon">
                    {item.icon}
                  </span>
                  <span className="left-nav-item-label">{item.label}</span>
                  {item.comingSoon && (
                    <span className="left-nav-item-soon">Soon</span>
                  )}
                </button>
              </React.Fragment>
            );
          })}
        </nav>

        {/* ─── Upcoming calls widget ─────────────────────────────────
            Renders the seller's next N calendar events where the bot
            is slated to join. Fictitious seed data for now — the
            auto-schedule poller will populate this from real calendar
            rows once the DTO is wired up. Gated on a non-empty list
            so the sidebar doesn't show an empty-state placeholder. */}
        <div className="left-nav-bottom">
        {/* Data widget, not navigation — hidden on the collapsed rail
            (an icon form would fake meaning it doesn't have).

            upcomingMeta non-null = the upcoming_calls_join flag is on:
            the widget shows the whole calendar (today + 2 days) with
            per-meeting Join links, a REC dot only where the notetaker
            will attend, and real empty states (connect / reconnect /
            nothing upcoming). upcomingMeta null = legacy widget below,
            untouched. */}
        {!collapsed && upcomingMeta && (
          <div className="left-nav-upcoming" aria-label="Upcoming calls">
            <div className="left-nav-upcoming-head">
              <span>Upcoming calls</span>
            </div>
            {!upcomingMeta.connected ? (
              <button
                type="button"
                className="left-nav-upcoming-connect"
                onClick={() => onNavigate({ kind: 'phase', target: 'integrations' })}
              >
                Connect your calendar →
              </button>
            ) : (
              <>
                {upcomingMeta.reconnectRequired.length > 0 && (
                  <button
                    type="button"
                    className="left-nav-upcoming-connect left-nav-upcoming-reconnect"
                    onClick={() => onNavigate({ kind: 'phase', target: 'integrations' })}
                    title={`Access to ${upcomingMeta.reconnectRequired.join(' and ')} was revoked or expired — reconnect to keep this list current.`}
                  >
                    Calendar needs reconnecting →
                  </button>
                )}
                {upcomingMeetings.length === 0 ? (
                  <div className="left-nav-upcoming-empty">
                    No calls in the next few days
                  </div>
                ) : (
                  <ul className="left-nav-upcoming-list">
                    {(() => {
                      const rows = [];
                      let lastDayKey = null;
                      const shown = upcomingMeetings.slice(0, 6);
                      shown.forEach((m, idx) => {
                        const key = dayBucketKey(m.startAt);
                        if (key !== lastDayKey) {
                          rows.push(
                            <li
                              key={`day-${key}-${idx}`}
                              className="left-nav-upcoming-daysep"
                              aria-hidden="true"
                            >
                              {formatMeetingDayHeader(m.startAt)}
                            </li>
                          );
                          lastDayKey = key;
                        }
                        const recording = m.notetaker === 'scheduled' || m.notetaker === 'recording';
                        rows.push(
                          <li key={m.id} className="left-nav-upcoming-row">
                            <span className="left-nav-upcoming-provider" title={m.provider}>
                              {PROVIDER_GLYPH[m.provider] || PROVIDER_GLYPH['google-meet']}
                            </span>
                            <span className="left-nav-upcoming-main">
                              <span className="left-nav-upcoming-title" title={m.title}>
                                {recording && (
                                  <span
                                    className="left-nav-upcoming-rec-dot left-nav-upcoming-row-dot"
                                    title="TrailerCast's notetaker will record this call"
                                    aria-label="Being recorded"
                                  />
                                )}
                                {m.title}
                              </span>
                              <span className="left-nav-upcoming-time">
                                {formatMeetingTime(m.startAt)}
                                {m.zoomPasscode ? ` · passcode ${m.zoomPasscode}` : ''}
                              </span>
                            </span>
                            {/* Straight to the provider's own join URL — the
                                exact link the calendar app's Join uses, no
                                TrailerCast page in between. */}
                            {m.meetingUrl && (
                              <a
                                className="left-nav-upcoming-join"
                                href={m.meetingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open this meeting in a new tab"
                              >
                                Join
                              </a>
                            )}
                          </li>
                        );
                      });
                      if (upcomingMeetings.length > shown.length) {
                        rows.push(
                          <li key="more" className="left-nav-upcoming-empty" aria-hidden="true">
                            …and {upcomingMeetings.length - shown.length} more
                          </li>
                        );
                      }
                      return rows;
                    })()}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
        {!collapsed && !upcomingMeta && upcomingMeetings && upcomingMeetings.length > 0 && (
          <div className="left-nav-upcoming" aria-label="Upcoming calls being recorded">
            <div className="left-nav-upcoming-head">
              <span>Upcoming calls</span>
              <span className="left-nav-upcoming-rec" title="Auto-recording via TrailerCast">
                <span className="left-nav-upcoming-rec-dot" aria-hidden="true" />
                REC
              </span>
            </div>
            <ul className="left-nav-upcoming-list">
              {(() => {
                // Group consecutive meetings by day so we can emit a
                // date separator the first time each day appears. The
                // `upcomingMeetings` array is expected to be
                // chronologically sorted — we don't re-sort here to
                // avoid stepping on the caller's ordering.
                const rows = [];
                let lastDayKey = null;
                upcomingMeetings.slice(0, 4).forEach((m, idx) => {
                  const key = dayBucketKey(m.startAt);
                  if (key !== lastDayKey) {
                    rows.push(
                      <li
                        key={`day-${key}-${idx}`}
                        className="left-nav-upcoming-daysep"
                        aria-hidden="true"
                      >
                        {formatMeetingDayHeader(m.startAt)}
                      </li>
                    );
                    lastDayKey = key;
                  }
                  rows.push(
                    <li key={m.id} className="left-nav-upcoming-row">
                      <span className="left-nav-upcoming-provider" title={m.provider}>
                        {PROVIDER_GLYPH[m.provider] || PROVIDER_GLYPH['google-meet']}
                      </span>
                      <span className="left-nav-upcoming-main">
                        <span className="left-nav-upcoming-title" title={m.title}>
                          {m.title}
                        </span>
                        <span className="left-nav-upcoming-time">
                          {formatMeetingTime(m.startAt)}
                        </span>
                      </span>
                    </li>
                  );
                });
                return rows;
              })()}
            </ul>
          </div>
        )}

        {/* Collapse toggle — pinned at the bottom, same position in
            both modes (2026-07-26 collapsible sidebar). Desktop-only
            via CSS; mobile keeps the drawer. Ctrl/Cmd+B also toggles
            (wired in App.jsx). */}
        {typeof onToggleCollapse === 'function' && (
          <button
            type="button"
            className="left-nav-collapse-toggle"
            onClick={onToggleCollapse}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-label={collapsed ? 'Expand (Ctrl+B)' : undefined}
            title={collapsed ? undefined : 'Collapse sidebar (Ctrl+B)'}
          >
            <span className="left-nav-collapse-chevron" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="11 17 6 12 11 7" />
                <polyline points="18 17 13 12 18 7" />
              </svg>
            </span>
            <span className="left-nav-item-label">Collapse</span>
          </button>
        )}
        </div>
      </aside>
    </>
  );
}
